import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    FiLayers, FiSearch, FiMonitor, FiFileText,
    FiArrowRight, FiBookOpen, FiUser, FiCalendar
} from 'react-icons/fi';
import { fetchAPI } from '../../utils/api';
import toast from 'react-hot-toast';

const StudentStudyPacks = () => {
    const navigate = useNavigate();
    const [packs, setPacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        loadPacks();
    }, []);

    const loadPacks = async () => {
        try {
            const data = await fetchAPI('/student/get_all_study_packs.php');
            setPacks(data);
        } catch (error) {
            toast.error('Failed to load study packs');
        } finally {
            setLoading(false);
        }
    };

    const filteredPacks = packs.filter(p =>
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.teacher_name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="min-h-[calc(100vh-8rem)]">
            {/* Header / Search */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
                <div>
                    <h1 className="text-3xl font-black text-gray-900 tracking-tight uppercase italic">
                        Study <span className="text-blue-600">Packs</span>
                    </h1>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1">
                        Free resources for all students
                    </p>
                </div>

                <div className="relative w-full md:w-80 group">
                    <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    <input
                        type="text"
                        placeholder="SEARCH MODULES..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-white border border-gray-100 rounded-xl text-xs font-bold uppercase tracking-widest focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500/50 outline-none transition-all"
                    />
                </div>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                    <div className="w-10 h-10 border-2 border-blue-500/10 border-t-blue-600 rounded-full animate-spin"></div>
                    <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-400">Scanning Vault...</p>
                </div>
            ) : filteredPacks.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredPacks.map((pack) => (
                        <motion.div
                            key={pack.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="group bg-white rounded-xl border border-gray-100 p-8 hover:shadow-2xl hover:shadow-blue-900/5 transition-all duration-500 relative overflow-hidden font-display"
                        >
                            {/* Abstract Decor */}
                            <div className="absolute -top-10 -right-10 w-40 h-40 bg-blue-50/50 rounded-full blur-3xl group-hover:bg-blue-100 transition-colors"></div>

                            {/* Badge */}
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-50 rounded-full mb-6">
                                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                                <span className="text-[10px] font-black text-green-600 uppercase tracking-widest">FREE ACCESS</span>
                            </div>

                            <div className="relative z-10">
                                <p className="text-[10px] font-black text-blue-600 uppercase tracking-[0.2em] mb-2">{pack.subject}</p>
                                <h3 className="text-xl font-bold text-gray-900 uppercase tracking-tight mb-4 group-hover:text-blue-600 transition-colors line-clamp-2 min-h-[56px]">
                                    {pack.title}
                                </h3>

                                <div className="flex items-center gap-3 mb-8">
                                    <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400">
                                        <FiUser size={14} />
                                    </div>
                                    <p className="text-xs font-bold text-gray-500 uppercase tracking-tight">{pack.teacher_name}</p>
                                </div>

                                <div className="grid grid-cols-2 gap-4 mb-8">
                                    <div className="bg-gray-50/50 rounded-xl p-4 border border-transparent group-hover:border-blue-100 transition-all font-display">
                                        <div className="flex items-center gap-2 text-gray-400 mb-1">
                                            <FiMonitor size={12} />
                                            <span className="text-[9px] font-black uppercase tracking-widest">LESSONS</span>
                                        </div>
                                        <p className="text-lg font-black text-gray-900 tracking-tighter">{pack.lessons_count}</p>
                                    </div>
                                    <div className="bg-gray-50/50 rounded-xl p-4 border border-transparent group-hover:border-blue-100 transition-all font-display">
                                        <div className="flex items-center gap-2 text-gray-400 mb-1">
                                            <FiFileText size={12} />
                                            <span className="text-[9px] font-black uppercase tracking-widest">TUTES</span>
                                        </div>
                                        <p className="text-lg font-black text-gray-900 tracking-tighter">{pack.tutes_count}</p>
                                    </div>
                                </div>

                                <button
                                    onClick={() => navigate(`/dashboard/study-packs/view/${pack.id}`)}
                                    className="w-full py-4 bg-gray-900 text-white rounded-xl font-black uppercase tracking-widest text-[10px] flex items-center justify-center gap-3 group-hover:bg-blue-600 transition-all shadow-xl shadow-gray-900/10 group-hover:shadow-blue-600/20"
                                >
                                    OPEN MODULE <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                                </button>
                            </div>
                        </motion.div>
                    ))}
                </div>
            ) : (
                <div className="py-20 text-center flex flex-col items-center gap-6 opacity-20">
                    <FiBookOpen size={64} />
                    <p className="text-xs font-black uppercase tracking-[0.4em]">NO_MODULES_DETECTED</p>
                </div>
            )}
        </div>
    );
};

export default StudentStudyPacks;
